import { useEffect, useRef } from 'react'
import { lang } from '@lib/constants'

interface ElevarSettings {
  proxyPath?: string
}

interface ElevarConfig {
  script_src_custom_pages: string
  script_src_custom_pages_proxied: string
}

interface ElevarHandler {
  handler: (config: ElevarConfig, settings: ElevarSettings) => Promise<void>
}

// Elevar config URLs for different markets
const ELEVAR_CONFIG_URLS = {
  'en-us': "https://shopify-gtm-suite.getelevar.com/configs/18964df798cfd02706125dcf1aa6265e19ebf8c8/config.js",
  'en-gb': "https://shopify-gtm-suite.getelevar.com/configs/809ac1e43333834171ba13a194bf371648e3f21e/config.js"
}

let isInitialized = false

const initializeElevar = async () => {
  if (isInitialized) {
    // console.log('[Elevar] Already initialized')
    return
  }

  const settings: ElevarSettings = {}
  const configUrl = ELEVAR_CONFIG_URLS[lang as keyof typeof ELEVAR_CONFIG_URLS]
  
  if (!configUrl) {
    console.warn(`[Elevar] No config found for language: ${lang}`)
    return
  }
  
  try {
    // Use type assertion for the dynamic import
    const configModule = await import(/* webpackIgnore: true */ configUrl)
    const config = configModule.default as ElevarConfig
    
    const scriptUrl = settings.proxyPath
      ? settings.proxyPath + config.script_src_custom_pages_proxied
      : config.script_src_custom_pages

    if (scriptUrl) {
      const handlerModule = await import(/* webpackIgnore: true */ scriptUrl) as ElevarHandler
      await handlerModule.handler(config, settings)
      
      // Enable debug mode after initialization
      if (typeof window !== 'undefined') {
        window?.ElevarInvalidateContext?.()
        // window.ElevarDebugMode(true)
        // console.log('[Elevar] Debug mode enabled')
        isInitialized = true
      }
    }
  } catch (error) {
    console.error('Failed to initialize Elevar:', error)
  }
}

const useElevar = () => {
  const initialized = useRef(false)

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      initializeElevar()
    }
  }, [])
}

export default useElevar 